/**
 * ปิดงาน / ปิดใบ / ยกเลิกใบสั่งผลิต — `POST /v1/production-orders/{id}/complete|close|cancel` (`WO-P1-M5-002`)
 *
 * กติกาเดียวกับ `ProductionOrderReleasePanel`: `expected_status` อ่านจากใบที่แสดงอยู่ตอนกด, `409` ไม่ลองใหม่
 * แต่ดึงของจริงมาแสดงหนึ่งครั้ง, ความล้มเหลวไม่ขยับสถานะบนจอ, แถบนี้อยู่ทุกสถานะเพื่อให้ผลของคำสั่งไม่หาย
 *
 * สิทธิ์ (ตามเซิร์ฟเวอร์): complete = EDIT, close/cancel = APPROVE — ปุ่มที่ซ่อนเป็นความสะดวก ไม่ใช่ด่านความปลอดภัย
 * close และ cancel เป็นสถานะสุดท้ายย้อนไม่ได้ จึงต้องกดยืนยันอีกครั้ง
 */

import { useEffect, useRef, useState } from 'react';

import { ProductionOrderFailureNotice } from './ProductionOrderFailureNotice';
import { orderStatusLabel } from './productionOrderView';
import { StatusBadge } from '../StatusBadge';
import {
  fetchProductionOrder,
  transitionProductionOrder,
  type ProductionOrderFailure,
  type ProductionOrderTransition,
} from '../../lib/productionOrders/client';
import {
  CANCELLABLE_STATUSES,
  CLOSABLE_STATUS,
  COMPLETABLE_STATUS,
  type ProductionOrder,
} from '../../lib/productionOrders/types';

export interface ProductionOrderTransitionPanelProps {
  readonly order: ProductionOrder;
  readonly canEdit: boolean;
  readonly canApprove: boolean;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly onOrderChanged: (order: ProductionOrder) => void;
}

interface Command {
  readonly id: ProductionOrderTransition;
  readonly label: string;
  readonly working: string;
  readonly done: string;
  /** สถานะสุดท้าย ย้อนไม่ได้ → ต้องยืนยัน */
  readonly terminal: boolean;
  readonly explain: string;
}

const COMMANDS: Readonly<Record<ProductionOrderTransition, Command>> = {
  complete: {
    id: 'complete',
    label: 'ปิดงานผลิต',
    working: 'กำลังปิดงาน…',
    done: 'ปิดงานผลิตแล้ว',
    terminal: false,
    explain: 'กำลังผลิต → ผลิตเสร็จ — เซิร์ฟเวอร์ปฏิเสธถ้ายังมีงานที่สแกนเข้าแล้วแต่ยังไม่สแกนออก',
  },
  close: {
    id: 'close',
    label: 'ปิดใบสั่งผลิต',
    working: 'กำลังปิดใบ…',
    done: 'ปิดใบสั่งผลิตแล้ว',
    terminal: true,
    explain:
      'ผลิตเสร็จ → ปิดแล้ว — เป็นสถานะสุดท้าย หลังปิดจะสแกนงานเข้าใบนี้ไม่ได้อีก (ผลต่างต้นทุนดูได้ที่เมนู "ต้นทุนและผลต่าง")',
  },
  cancel: {
    id: 'cancel',
    label: 'ยกเลิกใบสั่งผลิต',
    working: 'กำลังยกเลิก…',
    done: 'ยกเลิกใบสั่งผลิตแล้ว',
    terminal: true,
    explain:
      'ร่าง/สั่งปล่อยแล้ว → ยกเลิก — เป็นสถานะสุดท้าย ใบที่มีงานยืนยันผลไปแล้วยกเลิกไม่ได้ ต้องกลับรายการแทน',
  },
};

/** คำสั่งที่ใบในสถานะนี้มี และบัญชีนี้มีสิทธิ์กด */
function availableTransitions(
  status: string,
  canEdit: boolean,
  canApprove: boolean,
): readonly ProductionOrderTransition[] {
  const out: ProductionOrderTransition[] = [];
  if (status === COMPLETABLE_STATUS && canEdit) out.push('complete');
  if (status === CLOSABLE_STATUS && canApprove) out.push('close');
  if ((CANCELLABLE_STATUSES as readonly string[]).includes(status) && canApprove) out.push('cancel');
  return out;
}

type PanelState =
  | { readonly status: 'idle' }
  | { readonly status: 'confirming'; readonly command: ProductionOrderTransition }
  | { readonly status: 'working'; readonly command: ProductionOrderTransition }
  | { readonly status: 'done'; readonly command: ProductionOrderTransition; readonly resultStatus: string }
  | {
      readonly status: 'failed';
      readonly failure: ProductionOrderFailure;
      readonly refreshed: 'ok' | 'failed' | null;
    };

export function ProductionOrderTransitionPanel({
  order,
  canEdit,
  canApprove,
  apiBaseUrl,
  fetchImpl,
  onOrderChanged,
}: ProductionOrderTransitionPanelProps) {
  const [state, setState] = useState<PanelState>({ status: 'idle' });
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const commands = availableTransitions(order.status, canEdit, canApprove);
  const busy = state.status === 'working';

  const run = async (command: ProductionOrderTransition) => {
    if (busy) return;
    const expectedStatus = order.status; // อ่านตอนกด จากใบที่แสดงอยู่
    setState({ status: 'working', command });
    const result = await transitionProductionOrder(order.production_order_id, command, expectedStatus, {
      apiBaseUrl,
      fetchImpl,
    });
    if (!mounted.current || result.status === 'cancelled') return;
    if (result.status === 'ready') {
      setState({ status: 'done', command, resultStatus: result.order.status });
      onOrderChanged(result.order);
      return;
    }
    setState({ status: 'failed', failure: result, refreshed: null });
    if (result.status !== 'conflict') return;
    const refreshed = await fetchProductionOrder(order.production_order_id, { apiBaseUrl, fetchImpl });
    if (!mounted.current) return;
    if (refreshed.status === 'ready') {
      onOrderChanged(refreshed.order);
      setState({ status: 'failed', failure: result, refreshed: 'ok' });
      return;
    }
    setState({ status: 'failed', failure: result, refreshed: 'failed' });
  };

  const press = (command: ProductionOrderTransition) => {
    if (COMMANDS[command].terminal) {
      setState({ status: 'confirming', command });
      return;
    }
    void run(command);
  };

  return (
    <>
      <section className="cap-card" aria-label="ปิดงาน ปิดใบ หรือยกเลิก" data-testid="production-order-transitions">
        <div className="cap-card__head">
          <h2 className="cap-card__title">ปิดงาน / ปิดใบ / ยกเลิก</h2>
        </div>
        {commands.length === 0 ? (
          <p className="cap-card__body" data-testid="production-order-transitions-none">
            ใบที่สถานะ “{orderStatusLabel(order.status)}” ไม่มีคำสั่งให้บัญชีนี้กดในส่วนนี้
            {order.status === 'RELEASED' && canEdit && !canApprove
              ? ' — ใบที่สั่งปล่อยแล้วจะเข้าสถานะ "กำลังผลิต" เมื่อหน้างานสแกนเข้าครั้งแรก'
              : ''}
          </p>
        ) : (
          <div className="cap-card__body">
            {commands.map((id) => (
              <p key={id} className="cap-notice__note">
                <strong>{COMMANDS[id].label}:</strong> {COMMANDS[id].explain}
              </p>
            ))}
          </div>
        )}

        {state.status === 'confirming' ? (
          <div className="cap-notice" role="alertdialog" data-testid="production-order-transition-confirm">
            <StatusBadge tone="warning">ย้อนไม่ได้</StatusBadge>
            <h3 className="cap-notice__title">
              ยืนยัน{COMMANDS[state.command].label} {order.order_no}?
            </h3>
            <p className="cap-notice__text">{COMMANDS[state.command].explain}</p>
            <div className="cap-form__actions">
              <button
                type="button"
                className="cap-button cap-button--primary"
                data-testid="production-order-transition-confirm-yes"
                onClick={() => {
                  void run(state.command);
                }}
              >
                ยืนยัน{COMMANDS[state.command].label}
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="production-order-transition-confirm-no"
                onClick={() => setState({ status: 'idle' })}
              >
                ไม่ทำ
              </button>
            </div>
          </div>
        ) : null}

        {commands.length > 0 && state.status !== 'confirming' ? (
          <div className="cap-card__foot">
            <div className="cap-form__actions">
              {commands.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`cap-button ${id === 'cancel' ? 'cap-button--ghost' : 'cap-button--primary'}`}
                  data-testid={`production-order-${id}-submit`}
                  disabled={busy}
                  onClick={() => press(id)}
                >
                  {state.status === 'working' && state.command === id ? COMMANDS[id].working : COMMANDS[id].label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {state.status === 'done' ? (
        <div className="cap-banner" role="status" data-testid="production-order-transition-success">
          <strong>{COMMANDS[state.command].done}</strong>
          <span>
            สถานะล่าสุดที่เซิร์ฟเวอร์ยืนยันคือ {orderStatusLabel(state.resultStatus)} ({state.resultStatus})
          </span>
        </div>
      ) : null}

      {state.status === 'failed' ? (
        <>
          <ProductionOrderFailureNotice
            failure={state.failure}
            context="transition"
            plantCode={order.plant_code ?? undefined}
          />
          {state.refreshed === null ? null : (
            <p className="cap-banner__note" data-testid="production-order-transition-refresh">
              {state.refreshed === 'ok'
                ? 'ดึงข้อมูลล่าสุดของใบนี้มาแสดงแทนแล้ว และไม่ได้ส่งคำสั่งเดิมซ้ำ — ตรวจสถานะจริงก่อนตัดสินใจใหม่'
                : 'ดึงข้อมูลล่าสุดไม่สำเร็จ สิ่งที่เห็นบนหน้าจออาจไม่ใช่สถานะจริงแล้ว — เปิดหน้านี้ใหม่ก่อนตัดสินใจ'}
            </p>
          )}
        </>
      ) : null}
    </>
  );
}
